"use strict";
/**
 * What "the user's code" is — the one definition every export sink applies.
 *
 * The pod workspace at /home/project is the user's app PLUS everything the
 * agent and the pod need: BASE.md and skills/ (agent playbooks), start.sh and
 * .dunea/ (pod helpers, agent memory), Dunea's two platform scripts in
 * index.html, and config that is only correct inside a pod (an absolute pnpm
 * store path, Dunea's Cloudflare account id). Four ignore lists already exist
 * (the pod watcher, readProjectFiles, getProjectFiles, the snapshot
 * .gitignore) and each answers a different question. This module answers
 * exactly one: what leaves the workspace when the code is handed over.
 *
 * Pure and isomorphic on purpose — no fs, no Node APIs — so studio applies it
 * to a tar stream and the client applies it to the Code tab's file tree, and
 * the two can never disagree about what the user owns.
 *
 * Paths are workspace-relative, `/`-separated, no leading `./` or `/`. Use
 * `toWorkspacePath` to normalise anything else first.
 */
Object.defineProperty(exports, "__esModule", { value: true });
exports.EXPORT_REWRITTEN_FILES = exports.EXPORT_DROPPED_FILES = exports.EXPORT_DROPPED_ROOT_ENTRIES = exports.EXPORT_EXCLUDED_DIRS = exports.WORKSPACE_ROOT = void 0;
exports.toWorkspacePath = toWorkspacePath;
exports.isExportable = isExportable;
exports.exportTarExcludeArgs = exportTarExcludeArgs;
exports.isRewrittenOnExport = isRewrittenOnExport;
exports.rewriteForExport = rewriteForExport;
exports.exportArchiveName = exportArchiveName;
exports.buildExportReadme = buildExportReadme;
/** The pod workspace root. Mirrors WORK_DIR in infra and the client. */
exports.WORKSPACE_ROOT = '/home/project';
/**
 * Directory names dropped wherever they appear. These are the size hazards
 * (node_modules, the pnpm store, local D1 state) and the one credential leak
 * (.git — the pod's .git/config carries the shared deploy token in its origin
 * URL). They double as `tar --exclude` arguments so the pod never serialises
 * them in the first place; the classifier below still drops them if they slip
 * through, because the tar exclude is an optimisation and this is the rule.
 */
exports.EXPORT_EXCLUDED_DIRS = [
    'node_modules',
    '.git',
    '.pnpm-store',
    '.npm',
    '.yarn',
    '.cache',
    '.local',
    '.ssh',
    '.wrangler',
    'dist',
    'build',
    '.next',
    '.nuxt',
    '__pycache__',
    'venv',
    '.venv',
];
/**
 * Agent and pod scaffolding at the workspace root. Never part of the site:
 * BASE.md is the agent's build instructions, skills/ its playbooks, start.sh a
 * supervisord helper that references pod-only services, .dunea/ the agent's
 * clone-brief memory.
 */
exports.EXPORT_DROPPED_ROOT_ENTRIES = ['BASE.md', 'skills', 'start.sh', '.dunea'];
/**
 * Dunea's own assets in the template's public/ dir. Every one of these is
 * Dunea showing through on a site the user is taking somewhere else, so the
 * file goes AND its reference in index.html goes with it (see
 * `rewriteForExport`):
 *
 *  - `dunea-selector.js` — the workbench element picker, inert outside the
 *    Dunea iframe.
 *  - `dunea-analytics.js` — the beacon to analytics.dunea.app.
 *  - `dunea-badge.js` — the "Made with Dunea" pill, with a utm-tagged link
 *    back to dunea.ai. Legacy projects have it baked into their workspace
 *    (current deploys inject it at build time instead), and shipping it would
 *    put our marketing on their site — the one thing an export must not do.
 *  - `dunea-mark.svg` — our logo, wired up as the template's favicon. A
 *    self-hosted site would show Dunea's mark in the browser tab.
 */
exports.EXPORT_DROPPED_FILES = [
    'frontend/public/dunea-selector.js',
    'frontend/public/dunea-analytics.js',
    'frontend/public/dunea-badge.js',
    'frontend/public/dunea-mark.svg',
    // Preview-only plugin backend vars (studio URL + app token), written by Dunea.
    'frontend/.dev.vars',
];
/**
 * Basename patterns dropped anywhere: core dumps, logs, editor leftovers,
 * secrets. `.env.example` is the one .env* that ships — it documents the
 * variables a self-hoster must set.
 */
const DROPPED_BASENAME_PATTERNS = [
    /^core$/,
    /^core\..+$/,
    /\.core$/,
    /\.log$/,
    /\.pid$/,
    /\.seed$/,
    /^\.DS_Store$/,
    /^Thumbs\.db$/,
    /\.pyc$/,
    /\.pyo$/,
    /\.egg-info$/,
    /\.bak$/,
    /\.orig$/,
    /\.tmp$/,
    /~$/,
    /^\.git-credentials$/,
    /^\.netrc$/,
    /^\.gitconfig$/,
];
const EXCLUDED_DIR_SET = new Set(exports.EXPORT_EXCLUDED_DIRS);
const DROPPED_ROOT_SET = new Set(exports.EXPORT_DROPPED_ROOT_ENTRIES);
const DROPPED_FILE_SET = new Set(exports.EXPORT_DROPPED_FILES);
/**
 * Normalise a path to workspace-relative form. Accepts the absolute pod path
 * (`/home/project/frontend/src/App.tsx`), tar member names (`./frontend/...`),
 * and already-relative paths. Returns '' for the workspace root itself.
 */
function toWorkspacePath(path, workspaceRoot = exports.WORKSPACE_ROOT) {
    let p = path.replace(/\\/g, '/');
    if (p === workspaceRoot) {
        return '';
    }
    if (p.startsWith(`${workspaceRoot}/`)) {
        p = p.slice(workspaceRoot.length + 1);
    }
    return p.replace(/^(\.\/|\/)+/, '').replace(/\/+$/, '');
}
function isDroppedBasename(name) {
    if (name === '.env' || (name.startsWith('.env.') && name !== '.env.example')) {
        return true;
    }
    return DROPPED_BASENAME_PATTERNS.some((re) => re.test(name));
}
/**
 * Whether a workspace-relative path (file or directory) is part of the user's
 * code. The workspace root ('') is not exportable — it has no name to ship
 * under; callers place entries inside their own top-level folder.
 */
function isExportable(relativePath) {
    if (!relativePath) {
        return false;
    }
    const segments = relativePath.split('/');
    if (segments.some((s) => EXCLUDED_DIR_SET.has(s))) {
        return false;
    }
    if (DROPPED_ROOT_SET.has(segments[0])) {
        return false;
    }
    if (DROPPED_FILE_SET.has(relativePath)) {
        return false;
    }
    // Any ancestor named like a dropped entry (e.g. a `core.1234/` dir, an
    // `*.egg-info/` dir) takes its contents with it.
    return !segments.some(isDroppedBasename);
}
/** `tar --exclude=…` arguments matching `EXPORT_EXCLUDED_DIRS`. */
function exportTarExcludeArgs() {
    return exports.EXPORT_EXCLUDED_DIRS.map((dir) => `--exclude=${dir}`);
}
/** Files whose exported bytes differ from the workspace's. */
exports.EXPORT_REWRITTEN_FILES = [
    'frontend/.npmrc',
    'frontend/wrangler.jsonc',
    'frontend/index.html',
    'frontend/package.json',
    'frontend/vite.config.ts',
];
const REWRITTEN_FILE_SET = new Set(exports.EXPORT_REWRITTEN_FILES);
/** True when `rewriteForExport` may change this file — decode it as text. */
function isRewrittenOnExport(relativePath) {
    return REWRITTEN_FILE_SET.has(relativePath);
}
/**
 * Surgical rewrites for files that are correct in the pod and wrong anywhere
 * else. Returns the exported text, or `null` when the file should be dropped
 * because nothing meaningful is left of it. Every other path is returned
 * unchanged — `frontend/src` and `frontend/worker` ship byte-identical.
 *
 * Two jobs: strip Dunea from the handover, and make the project the user's own
 * rather than a copy of our template.
 *
 *  - `frontend/.npmrc` loses `store-dir=/home/project/.pnpm-store`: an
 *    absolute pod path, and `pnpm install` on a laptop tries to create it.
 *  - `frontend/wrangler.jsonc` loses `account_id` (Dunea's Cloudflare account,
 *    which makes the user's own `wrangler deploy` fail) and is renamed from
 *    `dunea-app` / `dunea-app-db` to the project slug — otherwise a
 *    self-hoster's first deploy publishes a Worker called "dunea-app". The
 *    `local-placeholder` database_id stays: local dev works with it and a real
 *    deploy provisions its own D1. Comment sentences that name Dunea or
 *    describe our own pipeline go — they reference a key we just removed, a
 *    service the user doesn't have, and us.
 *  - `frontend/package.json` is renamed off `dunea-frontend` to the slug.
 *  - `frontend/index.html` loses every Dunea reference: the selector,
 *    analytics and badge <script> tags, the `dunea-mark.svg` favicon link, the
 *    `dunea-spa-base` meta (a hint to our static-preview server, meaningless
 *    anywhere else) and any HTML comment that explains our internals.
 *  - `frontend/vite.config.ts` loses the pod-only `server` block. Those values
 *    exist because the workspace runs inside a container behind our proxy
 *    (`host: 0.0.0.0`, `allowedHosts: true`) with HMR deliberately off; left
 *    in, the user's own `pnpm dev` has no hot reload and binds every
 *    interface. Vite's defaults are what a laptop wants.
 */
/**
 * Vocabulary that marks an HTML comment as ours rather than the user's. Keep it
 * in step with the template's own comments: anything Dunea-internal that a
 * reader of the exported project could not act on belongs here.
 */
const DUNEA_INTERNAL_COMMENT_RE = /dunea|static-preview|path-rewrite|chatId|preview server/i;
function rewriteForExport(relativePath, content, naming) {
    switch (relativePath) {
        case 'frontend/.npmrc': {
            const kept = content.split(/\r?\n/).filter((line) => !/^\s*store-dir\s*=/.test(line));
            const text = kept.join('\n');
            return text.trim() ? text : null;
        }
        case 'frontend/wrangler.jsonc': {
            let out = stripJsoncKey(content, 'account_id');
            out = stripJsoncCommentSentencesMatching(out, /Dunea|account_id|by studio|In-pod dev/);
            out = replaceJsoncStringValue(out, 'name', naming.slug);
            return replaceJsoncStringValue(out, 'database_name', `${naming.slug}-db`);
        }
        case 'frontend/package.json':
            return replaceJsoncStringValue(content, 'name', naming.slug);
        case 'frontend/index.html':
            return (content
                // Dunea's three platform scripts, whichever ones this project has.
                .replace(/^[ \t]*<script\b[^>]*\bsrc=["']\/dunea-(?:selector|analytics|badge)\.js["'][^>]*>\s*<\/script>[ \t]*\r?\n?/gm, '')
                // Our logo as their favicon.
                .replace(/^[ \t]*<link\b[^>]*\bhref=["']\/dunea-mark\.svg["'][^>]*>[ \t]*\r?\n?/gm, '')
                // A marker only our preview server reads.
                .replace(/^[ \t]*<meta\b[^>]*\bname=["']dunea-spa-base["'][^>]*>[ \t]*\r?\n?/gm, '')
                // Comments explaining Dunea's plumbing. These document the tags above,
                // so a comment left behind after its tag is removed is worse than the
                // tag was — the export shipped a paragraph about our preview server
                // pointing at nothing. Matched by vocabulary rather than by adjacency
                // to a removed tag, because the template's own comment never says
                // "Dunea" and a user's comment can sit in exactly the same place.
                .replace(/^[ \t]*<!--[^]*?-->[ \t]*\r?\n?/gm, (match) => (DUNEA_INTERNAL_COMMENT_RE.test(match) ? '' : match)));
        case 'frontend/vite.config.ts':
            return removeObjectProperty(content, 'server');
        default:
            return content;
    }
}
/**
 * Replace one top-level `"key": "value"` string value in a JSON/JSONC
 * document, leaving formatting and comments untouched. Only the first match is
 * replaced (these are top-level identity keys, of which there is one each).
 */
function replaceJsoncStringValue(source, key, value) {
    return source.replace(new RegExp(`("${key}"\\s*:\\s*)"[^"]*"`), `$1"${value}"`);
}
/**
 * Drop the comment SENTENCES that mention Dunea's own pipeline from a JSONC
 * document, leaving every other comment untouched.
 *
 * Sentence-scoped, not line-scoped and not block-scoped, because these comments
 * are prose wrapped at 80 columns: dropping only the matching line leaves a
 * dangling half-sentence, and dropping the whole contiguous block takes the
 * architectural note the reader still wants with it. A sentence runs from after
 * the previous comment line that ended in `.`/`:`/`!`/`?` up to and including
 * the next line that ends in one.
 */
function stripJsoncCommentSentencesMatching(source, pattern) {
    const lines = source.split(/\r?\n/);
    const isComment = (i) => i >= 0 && i < lines.length && /^\s*\/\//.test(lines[i]);
    const endsSentence = (i) => /[.!?:]\s*$/.test(lines[i]);
    const keep = lines.map(() => true);
    for (let i = 0; i < lines.length; i++) {
        if (!isComment(i) || !pattern.test(lines[i])) {
            continue;
        }
        let from = i;
        while (isComment(from - 1) && !endsSentence(from - 1)) {
            from--;
        }
        let to = i;
        while (!endsSentence(to) && isComment(to + 1)) {
            to++;
        }
        for (let j = from; j <= to; j++) {
            keep[j] = false;
        }
    }
    return lines.filter((_, i) => keep[i]).join('\n');
}
/**
 * Remove a `key: { … },` property from a JS/TS object literal by matching
 * braces, so a nested block goes with its contents. Returns the source
 * unchanged when the key isn't there. Brace counting is enough here: the
 * values in question are plain config literals with no braces inside strings.
 */
function removeObjectProperty(source, key) {
    const match = new RegExp(`^[ \\t]*${key}\\s*:\\s*\\{`, 'm').exec(source);
    if (!match) {
        return source;
    }
    let depth = 0;
    let i = match.index + match[0].length - 1;
    for (; i < source.length; i++) {
        if (source[i] === '{') {
            depth++;
        }
        else if (source[i] === '}') {
            depth--;
            if (depth === 0) {
                break;
            }
        }
    }
    if (depth !== 0) {
        return source;
    }
    let end = i + 1;
    if (source[end] === ',') {
        end++;
    }
    while (source[end] === ' ' || source[end] === '\t') {
        end++;
    }
    if (source[end] === '\r') {
        end++;
    }
    if (source[end] === '\n') {
        end++;
    }
    return source.slice(0, match.index) + source.slice(end);
}
/**
 * Remove one top-level `"key": "value",` line from a JSONC document without
 * touching anything else. The removed line normally carries the trailing comma
 * itself; when it was the last property (no comma), the comma is instead
 * stripped from the nearest preceding property line so the object stays
 * valid. Comments in between are preserved untouched.
 */
function stripJsoncKey(source, key) {
    const lines = source.split(/\r?\n/);
    const keyRe = new RegExp(`^\\s*"${key}"\\s*:`);
    const idx = lines.findIndex((line) => keyRe.test(line));
    if (idx === -1) {
        return source;
    }
    const removed = lines[idx];
    lines.splice(idx, 1);
    const hadTrailingComma = /,\s*(\/\/.*)?$/.test(removed);
    if (!hadTrailingComma) {
        for (let i = idx - 1; i >= 0; i--) {
            const line = lines[i];
            if (!line.trim() || /^\s*\/\//.test(line)) {
                continue;
            }
            lines[i] = line.replace(/,(\s*(\/\/.*)?)$/, '$1');
            break;
        }
    }
    return lines.join('\n');
}
/** Name the archive and its single top-level folder are given. */
function exportArchiveName(slug) {
    return `${slug}.zip`;
}
/**
 * The README a developer with no Dunea context follows. The exported
 * package.json has no migrate script (the pod runs the D1 migrate from
 * supervisord before vite), so the command is spelled out here.
 */
function buildExportReadme(opts) {
    const { projectName, slug } = opts;
    return `# ${projectName}

Built with [Dunea](https://dunea.ai). This is the complete source of the site:
a Vite + React front end and a Cloudflare Worker (Hono) API bound to a D1
database, in \`frontend/\`.

## Run it locally

You need Node.js 20+ and pnpm.

\`\`\`sh
cd frontend
pnpm install
pnpm exec wrangler d1 migrations apply DB --local
pnpm dev
\`\`\`

The migrate step builds the local SQLite database from \`migrations/\`. Run it
again whenever you add a migration (\`pnpm db:generate\` writes one from
\`worker/db/schema.ts\`).

## Deploy to your own Cloudflare account

\`\`\`sh
cd frontend
pnpm exec wrangler login
pnpm exec wrangler d1 create ${slug}-db
\`\`\`

Copy the \`database_id\` that prints into \`wrangler.jsonc\` (replacing
\`local-placeholder\`), then:

\`\`\`sh
pnpm exec wrangler d1 migrations apply DB --remote
pnpm build
pnpm exec wrangler deploy
\`\`\`

## What changed from the hosted version

- Every trace of Dunea is gone from the shipped site: the analytics beacon, the
  editor's element-selector script, the "Made with Dunea" badge and the Dunea
  favicon. Add your own icon with a \`<link rel="icon">\` in
  \`frontend/index.html\`.
- \`package.json\` and \`wrangler.jsonc\` are named after this project, and
  \`vite.config.ts\` uses Vite's own dev-server defaults (hot reload on) instead
  of the container settings the hosted workspace needed.
- Everything in \`frontend/src\` and \`frontend/worker\` is byte-for-byte what ran
  on Dunea.
- **Google sign-in will not work self-hosted.** The hosted site signs in
  through Dunea's OAuth broker, which only serves \`*.dunea.app\` hosts. The
  email/password sign-in in \`worker/dunea/auth.ts\` is self-contained and
  works anywhere. To keep Google, register your own OAuth client and point
  the Google branch of \`worker/dunea/auth.ts\` at it.
- Form submissions (\`worker/dunea/platform.ts\`) are written to this app's
  own D1 database and keep working as-is.
`;
}
